import React, { useMemo, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Content_Container from '@Elements/Content_Container';
import { H2 } from '@Elements/Fonts';

import { Expected_Working_Hours_Chart_Props } from '@Util/Types';

const expected_hours_per_day: number = 8;
const expected_hours_per_week: number = 40;
const expected_hours_per_month: number = 160;

export default function Expected_Working_Hours_Chart(props: Expected_Working_Hours_Chart_Props): JSX.Element {
    const [t] = useTranslation();   
    const [total_hours_state, set_total_hours_state] = useState(0);
    const [options, set_options] = useState<ApexOptions>({});

    const total_hours = useMemo(() => {
        let logs;

        if (props.data_view === 'day') {
            logs = props.todays_time_log;
        } else if (props.data_view === 'week') {
            logs = props.weeks_time_log;
        } else if (props.data_view === 'month') {
            logs = props.months_time_log;
        }

        return Array.isArray(logs) ? logs.reduce((total, log) => total + dayjs(log.endDate).diff(dayjs(log.startDate), 'hour', true), 0) : 0;
    }, [props.data_view, props.todays_time_log, props.weeks_time_log, props.months_time_log]);

    useEffect(() => {
        set_total_hours_state(total_hours);
    }, [total_hours]);

    const expected_hours = useMemo(() => {
        if (props.data_view === 'day') {
            return expected_hours_per_day;
        } else if (props.data_view === 'week') {
            return expected_hours_per_week;
        } else if (props.data_view === 'month') {
            return expected_hours_per_month;
        }
    }, [props.data_view]);

    const worked_percentage = Math.min(total_hours / expected_hours * 100, 100);

    useEffect(() => {
        set_options({
            chart: {
                type: 'donut',
                toolbar: {
                    show: false,
                },
            },
            stroke: {
                show: false,
            },
            tooltip: {
                enabled: false,
            },
            legend: {
                show: false,
            },
            colors: [worked_percentage === 100 ? '#2b952b' : '#44abe1', 'rgba(255, 255, 255, .2)'],
            labels: ['Worked', 'Remaining'],
            dataLabels: {
                enabled: false
            },
            plotOptions: {
                pie: {
                    donut: {
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: '22px',
                                fontFamily: 'Inter-Regular, sans-serif, serif',
                                color: '#929292',
                                offsetY: -10,
                            },
                            value: {
                                show: true,
                                fontSize: '16px',
                                fontFamily: 'Inter-Regular, sans-serif, serif',
                                color: '#929292',
                                offsetY: 16,
                                formatter: (val) => `${parseInt(val)} hours`,
                            },
                            total: {
                                show: true,
                                showAlways: true,
                                label: t('Text.RemainingTime'),
                                fontFamily: 'Inter-Bold, sans-serif, serif',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: "#fff",
                                formatter: (w) => {
                                    const remaining = expected_hours - total_hours;
                                    return `${Math.round(Math.max(remaining, 0))} ${t('Text.Hours')}`;
                                }
                            }
                        },
                    },
                },
            }
        });
    }, [total_hours, expected_hours, props.data_view]);

    const series = [total_hours, Math.max(expected_hours - total_hours, 0)];

    return(
        <section className="Module">
            <header className="Module_Header">
                <H2>{t('Text.WorkingTime')}</H2>
            </header>
            <Content_Container>
                <Chart 
                    key={total_hours_state}
                    options={options} 
                    series={series} 
                    type="donut" 
                />
            </Content_Container>
        </section>
    )
}